/**
 * Locale-aware text mask for date and time fields.
 *
 * Native `<input type="date">` takes its display order from the browser or OS
 * language, not from the page (Firefox in English shows mm/dd/yyyy on a
 * Brazilian community). The `Input` primitive draws a text field instead, and
 * this module is its pure core: the field layout for a locale, the mask applied
 * while typing, and the conversion between the displayed text and the ISO value
 * callers keep (`yyyy-mm-dd`, `hh:mm`, `yyyy-mm-ddThh:mm`).
 */

export type DateFieldType = 'date' | 'time' | 'datetime-local'

export const DATE_FIELD_TYPES: readonly string[] = ['date', 'time', 'datetime-local']

type Unit = 'day' | 'month' | 'year' | 'hour' | 'minute'

interface Segment {
  unit: Unit
  length: number
}

export interface FieldLayout {
  segments: Segment[]
  /** `separators[i]` goes between `segments[i]` and `segments[i + 1]`. */
  separators: string[]
  hour12: boolean
  /** Display labels for AM and PM, as the locale writes them. */
  meridiem: [string, string]
}

const REFERENCE = new Date(2001, 10, 22, 14, 5)

const clean = (s: string) => s.replace(/[\u00a0\u202f]/g, ' ')

const dateOrder = (locale: string): { units: Unit[]; separator: string } => {
  try {
    const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(REFERENCE)
    const units = parts.filter((p) => p.type === 'day' || p.type === 'month' || p.type === 'year').map((p) => p.type as Unit)
    const literal = parts.find((p) => p.type === 'literal')?.value.trim()
    if (units.length === 3 && literal) return { units, separator: clean(literal) }
  } catch {
    /* unknown locale: fall through to ISO order */
  }
  return { units: ['year', 'month', 'day'], separator: '-' }
}

const hourStyle = (locale: string): { hour12: boolean; meridiem: [string, string] } => {
  try {
    const format = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' })
    const cycle = (format.resolvedOptions() as { hourCycle?: string }).hourCycle
    if (cycle !== 'h11' && cycle !== 'h12') return { hour12: false, meridiem: ['AM', 'PM'] }
    const period = (h: number) =>
      clean(format.formatToParts(new Date(2001, 10, 22, h, 0)).find((p) => p.type === 'dayPeriod')?.value ?? '')
    const am = period(1)
    const pm = period(13)
    /* Typing "a" or "p" picks the period, so both labels must start that way. */
    if (/^a/i.test(am) && /^p/i.test(pm)) return { hour12: true, meridiem: [am, pm] }
  } catch {
    /* unknown locale: 24-hour clock */
  }
  return { hour12: false, meridiem: ['AM', 'PM'] }
}

export function buildLayout(type: DateFieldType, locale: string): FieldLayout {
  const segments: Segment[] = []
  const separators: string[] = []
  if (type !== 'time') {
    const { units, separator } = dateOrder(locale)
    units.forEach((unit, i) => {
      if (i > 0) separators.push(separator)
      segments.push({ unit, length: unit === 'year' ? 4 : 2 })
    })
  }
  const { hour12, meridiem } = hourStyle(locale)
  if (type !== 'date') {
    if (segments.length) separators.push(' ')
    segments.push({ unit: 'hour', length: 2 }, { unit: 'minute', length: 2 })
    separators.push(':')
  }
  return { segments, separators, hour12: type !== 'date' && hour12, meridiem }
}

export function placeholderOf(layout: FieldLayout, labels: Record<Unit, string>): string {
  const core = layout.segments.map((s, i) => (i > 0 ? layout.separators[i - 1] : '') + labels[s.unit]).join('')
  return layout.hour12 ? `${core} ${layout.meridiem[0]}/${layout.meridiem[1]}` : core
}

interface Scan {
  values: string[]
  pm: boolean | null
  /** The user typed a separator right after completing a segment. */
  trailingSeparator: boolean
}

const scan = (layout: FieldLayout, raw: string): Scan => {
  const values = layout.segments.map(() => '')
  let i = 0
  let pm: boolean | null = null
  let trailingSeparator = false
  for (const ch of raw) {
    trailingSeparator = false
    if (/\d/.test(ch)) {
      if (i >= values.length) continue
      values[i] += ch
      if (values[i].length === layout.segments[i].length) i++
    } else if (/[a-z]/i.test(ch)) {
      if (!layout.hour12) continue
      if (/a/i.test(ch)) pm = false
      else if (/p/i.test(ch)) pm = true
    } else if (ch.trim() || ch === ' ') {
      if (i >= values.length) continue
      const seg = layout.segments[i]
      /* "1/" means day 01; a year is never padded, it needs its four digits. */
      if (values[i].length > 0 && seg.unit !== 'year') {
        values[i] = values[i].padStart(seg.length, '0')
        i++
        trailingSeparator = true
      } else if (values[i].length === 0 && i > 0 && ch !== ' ') {
        trailingSeparator = true
      }
    }
  }
  return { values, pm, trailingSeparator }
}

const complete = (layout: FieldLayout, values: string[]) =>
  values.every((v, i) => v.length === layout.segments[i].length)

const render = (layout: FieldLayout, values: string[], pm: boolean | null, trailingSeparator: boolean): string => {
  let out = ''
  let k = 0
  for (; k < values.length && values[k]; k++) out += (k > 0 ? layout.separators[k - 1] : '') + values[k]
  if (trailingSeparator && k > 0 && k < values.length && values[k - 1].length === layout.segments[k - 1].length) {
    out += layout.separators[k - 1]
  }
  if (layout.hour12 && pm !== null && complete(layout, values)) out += ` ${layout.meridiem[pm ? 1 : 0]}`
  return out
}

/**
 * Applies the mask to what the field holds after a keystroke. `previous` is the
 * text before it, so deleting into the AM/PM label drops the label instead of
 * the scan reading its first letter back.
 */
export function maskText(layout: FieldLayout, raw: string, previous = ''): string {
  const { values, pm, trailingSeparator } = scan(layout, raw)
  if (layout.hour12 && previous && raw.length < previous.length && previous.startsWith(raw)) {
    const prev = scan(layout, previous)
    const core = render(layout, prev.values, null, false)
    if (prev.pm !== null && raw.length > core.length) return core
  }
  return render(layout, values, pm, trailingSeparator)
}

const pad = (n: number, len = 2) => String(n).padStart(len, '0')

/** The ISO value the text stands for, or '' while it is partial or not a real date. */
export function textToIso(layout: FieldLayout, type: DateFieldType, text: string): string {
  const { values, pm } = scan(layout, text)
  if (!complete(layout, values)) return ''
  const n: Partial<Record<Unit, number>> = {}
  layout.segments.forEach((s, i) => { n[s.unit] = Number(values[i]) })

  let date = ''
  if (type !== 'time') {
    const { year = 0, month = 0, day = 0 } = n
    if (year < 1 || month < 1 || month > 12 || day < 1) return ''
    if (day > new Date(Date.UTC(year, month, 0)).getUTCDate()) return ''
    date = `${pad(year, 4)}-${pad(month)}-${pad(day)}`
  }
  if (type === 'date') return date

  let hour = n.hour ?? -1
  const minute = n.minute ?? -1
  if (minute < 0 || minute > 59 || hour < 0 || hour > 23) return ''
  /* On a 12-hour clock, 1 to 12 needs AM or PM; 0 and 13 to 23 can only be 24-hour. */
  if (layout.hour12 && hour >= 1 && hour <= 12) {
    if (pm === null) return ''
    hour = (hour % 12) + (pm ? 12 : 0)
  }
  const time = `${pad(hour)}:${pad(minute)}`
  return type === 'time' ? time : `${date}T${time}`
}

const ISO = {
  date: /^(\d{4})-(\d{2})-(\d{2})$/,
  time: /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/,
  'datetime-local': /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/,
}

/** Normalizes a caller's value to the ISO form this field emits ('' when it is not one). */
export function normalizeIso(type: DateFieldType, value: unknown): string {
  if (typeof value !== 'string') return ''
  const m = ISO[type].exec(value.trim())
  if (!m) return ''
  if (type === 'date') return `${m[1]}-${m[2]}-${m[3]}`
  if (type === 'time') return `${m[1]}:${m[2]}`
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`
}

/** The text that shows an ISO value in the layout's locale. */
export function isoToText(layout: FieldLayout, type: DateFieldType, value: unknown): string {
  const iso = normalizeIso(type, value)
  if (!iso) return ''
  const [datePart, timePart] = type === 'time' ? ['', iso] : iso.split('T')
  const n: Partial<Record<Unit, string>> = {}
  if (datePart) [n.year, n.month, n.day] = datePart.split('-')
  let pm: boolean | null = null
  if (timePart) {
    const [h, mi] = timePart.split(':').map(Number)
    n.minute = pad(mi)
    if (layout.hour12) {
      pm = h >= 12
      n.hour = pad(h % 12 === 0 ? 12 : h % 12)
    } else {
      n.hour = pad(h)
    }
  }
  return render(layout, layout.segments.map((s) => n[s.unit] ?? ''), pm, false)
}

/** Where the caret goes after masking: just after the same count of digits as before it. */
export function caretAfterDigits(text: string, digits: number): number {
  if (digits <= 0) return 0
  let seen = 0
  for (let i = 0; i < text.length; i++) {
    if (/\d/.test(text[i]) && ++seen === digits) return i + 1
  }
  return text.length
}
